<template lang="pug">
.domain-options
  template(v-if="!hasPlan")
    .alert.alert-warning
      | ドメインを追加するには有料プラン登録が必要となります。
      a(href="https://client.sitelead.net/users/plans") プラン変更はこちら

  template(v-else)
    // ドメイン無効化アラート
    .alert.alert-warning.alert-dismissible.fade.show(v-if="showDomainAlert")
      | 現在登録ドメインは全て計測OFFになっておりますので、利用を継続するドメインの計測は
      a(href="/users/tag_domains" target="_blank") こちら
      | からONにしてください。
      button.close(type="button" @click="showDomainAlert = false" aria-label="Close")
        span(aria-hidden="true") &times;

    .mb-4(v-if="currentPlan")
      .card.p-3
        div
          strong オプション名:
          | {{ ' ' + currentPlan.name }}
        div
          strong.mr-2 ドメイン数:
          template(v-if="currentPlan.domain_unlimit")
            | 無制限
          template(v-else)
            | 追加 {{ currentPlan.additional_domains }} ドメイン
        div
          strong 金額:
          | {{ ' ' + formatCurrency(currentPlan.amount) + billingCyclePriceSuffix(currentPlan) }}
        .mt-2
          button.btn.btn-outline-danger.btn-sm(
            @click="cancelPlan"
            :disabled="isLoading"
          ) {{ isLoading && actionType === 'cancel' ? '処理中...' : '解約する' }}
    p(v-else) 現在ドメインオプションに加入していません。

    .table-responsive
      table.table.table-bordered
        thead.thead-light
          tr
            th オプション名
            th ドメイン数
            th 金額(税込)
            th 操作
        tbody
          tr(
            v-for="plan in plans"
            :key="plan.id"
            :class="{ 'table-active': plan.is_current }"
          )
            td {{ plan.name }}（{{ billingCycleLabel(plan) }}）
            td
              template(v-if="plan.domain_unlimit")
                | 無制限
              template(v-else)
                | 追加 {{ plan.additional_domains }} ドメイン
            td {{ formatCurrency(plan.amount) + billingCyclePriceSuffix(plan) }}
            td
              template(v-if="plan.is_current")
                button.btn.btn-success.btn-sm(disabled) 適用中
              template(v-else-if="currentPlanId")
                button.btn.btn-primary.btn-sm(
                  @click="changePlan(plan.id)"
                  :disabled="isLoading"
                ) {{ isLoading && actionType === 'change' && targetPlanId === plan.id ? '処理中...' : '変更する' }}
              template(v-else)
                button.btn.btn-success.btn-sm(
                  @click="addPlan(plan.id)"
                  :disabled="isLoading"
                ) {{ isLoading && actionType === 'add' && targetPlanId === plan.id ? '処理中...' : '加入する' }}

    .mt-4
      .card.p-3
        h5 ドメインオプションについて
        p
          | ※1. ドメインオプションに加入すると、各有料プラン（Basicなど）のドメイン数に、オプションのドメイン数が追加されます。
          br/
          | ※2. ドメインオプションの支払期間は各有料プランの支払期間と同期間となります。
          br/
          | ※3. 各有料プランを月払いから年払いに変更すると、ドメインオプションの適用期間も連動し変更されます。
          br/
          | ※4. ドメインオプションの初回の請求は即時請求され、2回目以降の請求は各有料プランと同じタイミングで請求されます。
          br/
          | 　 （ドメインオプションの初回請求額は、ドメインオプション申込日時から各有料プランの次回請求日時までの料金のみが請求されます）

  // 通知エリア
  .notification-container
</template>

<script>
import DomainOptionsApi from '../api/domain_options_api'

export default {
  name: 'DomainOptions',
  props: {
    userToken: {
      type: String,
      required: true
    },
    hasPlan: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      plans: [],
      currentPlanId: null,
      isLoading: false,
      actionType: null, // 'add', 'change', 'cancel'
      targetPlanId: null,
      showDomainAlert: false
    }
  },
  computed: {
    currentPlan() {
      return this.plans.find(plan => plan.is_current) || null
    }
  },
  async mounted() {
    if (this.hasPlan) {
      await this.loadPlans()
    }
  },
  methods: {
    async loadPlans() {
      try {
        const response = await DomainOptionsApi.getPlans(this.userToken)
        this.plans = response.plans
        this.currentPlanId = response.current_plan_id
      } catch (error) {
        this.showNotification('プラン情報の取得に失敗しました', 'danger')
        console.error('Error loading plans:', error)
      }
    },

    async addPlan(planId) {
      if (!confirm('このプランに加入しますか？')) return

      this.isLoading = true
      this.actionType = 'add'
      this.targetPlanId = planId

      try {
        const response = await DomainOptionsApi.addPlan(this.userToken, planId)
        this.showNotification(response.message || 'プランに加入しました', 'success')

        // プラン状態を更新
        this.updatePlanStates('add', planId)
        this.currentPlanId = planId
      } catch (error) {
        this.showNotification(error.message || 'エラーが発生しました', 'danger')
        console.error('Error adding plan:', error)
      } finally {
        this.isLoading = false
        this.actionType = null
        this.targetPlanId = null
      }
    },

    async changePlan(planId) {
      if (!confirm('プランを変更しますか？')) return

      this.isLoading = true
      this.actionType = 'change'
      this.targetPlanId = planId

      try {
        const response = await DomainOptionsApi.changePlan(this.userToken, planId)

        // toastメッセージを表示
        this.$toasted.success(response.message || 'プランを変更しました')

        // ドメインが無効化された場合はアラートを表示
        if (response.domains_invalidated) {
          this.showDomainAlert = true
        }

        // プラン状態を更新
        this.updatePlanStates('change', planId)
        this.currentPlanId = planId
      } catch (error) {
        this.showNotification(error.message || 'エラーが発生しました', 'danger')
        console.error('Error changing plan:', error)
      } finally {
        this.isLoading = false
        this.actionType = null
        this.targetPlanId = null
      }
    },

    async cancelPlan() {
      if (!confirm('本当に解約しますか？解約すると機能が制限されます。')) return

      this.isLoading = true
      this.actionType = 'cancel'

      try {
        const response = await DomainOptionsApi.cancelPlan(this.userToken)

        // toastメッセージを表示
        this.$toasted.success(response.message || 'プランを解約しました')

        // ドメインが無効化された場合はアラートを表示
        if (response.domains_invalidated) {
          this.showDomainAlert = true
        }

        // プラン状態を更新
        this.updatePlanStates('cancel')
        this.currentPlanId = null
      } catch (error) {
        this.showNotification(error.message || 'エラーが発生しました', 'danger')
        console.error('Error canceling plan:', error)
      } finally {
        this.isLoading = false
        this.actionType = null
      }
    },

    updatePlanStates(action, planId = null) {
      this.plans.forEach(plan => {
        if (action === 'cancel') {
          plan.is_current = false
        } else if (action === 'add' || action === 'change') {
          plan.is_current = (plan.id === planId)
        }
      })
    },

    billingCycleLabel(plan) {
      if (!plan) return ''
      if (plan.interval === 'year') return '年額'
      if (plan.interval === 'month') {
        const c = plan.interval_count
        if (c === 36) return '3年払い'
        if (c === 1) return '月額'
        if (c === 3) return '3ヶ月払い'
        if (c === 6) return '6ヶ月払い'
        return `${c}ヶ月払い`
      }
      return ''
    },

    billingCyclePriceSuffix(plan) {
      if (!plan) return ''
      if (plan.interval === 'year') return '/年'
      if (plan.interval === 'month') {
        const c = plan.interval_count
        if (c === 36) return '/3年'
        if (c === 1) return '/月'
        return `/${c}ヶ月`
      }
      return ''
    },

    formatCurrency(amount) {
      if (amount == null || amount === '') {
        return '-'
      }
      return new Intl.NumberFormat('ja-JP', {
        style: 'currency',
        currency: 'JPY',
        minimumFractionDigits: 0
      }).format(amount)
    },

    showNotification(message, type) {
      // Vue Toastedを使用した通知表示
      if (type === 'success') {
        this.$toasted.success(message)
      } else if (type === 'danger') {
        this.$toasted.error(message)
      } else if (type === 'warning') {
        this.$toasted.info(message)
      } else {
        this.$toasted.show(message)
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.domain-options {
  .table-active {
    background-color: rgba(0, 123, 255, 0.075);
  }

  .notification-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 9999;
  }
}
</style>
